import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import { SEO } from "@/components/common/SEO";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { Section } from "@/components/ui/Section";
import { Reveal } from "@/components/ui/Reveal";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { IMAGES } from "@/data/content";
import {
  HOW_WE_WORK,
  PARTNER_SECTIONS,
  SERVICE_DETAILS,
  SERVICE_GROUPS,
  SERVICES_HERO,
  SERVICES_INTRO,
  SERVICES_META,
  WHY_MEDCORE,
} from "@/data/services";
import { getIcon } from "@/lib/icons";

const groupTone = {
  "Build & Qualify": "bg-primary/5 text-primary border-primary/15",
  "Start & Deliver": "bg-secondary/5 text-secondary border-secondary/15",
  "Quality & Support": "bg-accent/5 text-accent border-accent/15",
};

export default function Services() {
  return (
    <>
      <SEO
        title={SERVICES_META.title}
        description={SERVICES_META.description}
        path="/services"
      />

      <section
        id="top"
        className="relative isolate overflow-hidden bg-brand-ink pt-28 text-white sm:pt-32 lg:pt-36"
        data-testid="services-hero"
      >
        <img
          src={IMAGES.hero}
          alt="Physician discussing a clinical research opportunity with a patient in a community healthcare setting"
          className="absolute inset-0 -z-20 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-brand-ink via-brand-ink/90 to-brand-emerald/65" />
        <Container className="pb-20 pt-10 sm:pb-24 lg:pb-28">
          <Reveal>
            <div className="max-w-4xl">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-teal-light">
                Clinical Research Site Management Services
              </p>
              <h1 className="mt-5 text-balance text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                {SERVICES_HERO.title}
              </h1>
              <div className="mt-6 max-w-3xl space-y-4 text-base leading-relaxed text-white/85 sm:text-lg">
                {SERVICES_HERO.paragraphs.map((paragraph) => (
                  <p key={paragraph.slice(0, 32)}>{paragraph}</p>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button as="link" to="/contact?inquiry=practice#contact-form" size="lg">
                  Become a MedCore Partner Site
                </Button>
                <Button
                  as="link"
                  to="/contact?inquiry=study#contact-form"
                  size="lg"
                  variant="outline"
                  className="border-white/40 bg-white/10 text-white hover:bg-white/20"
                >
                  Submit a Study Opportunity
                </Button>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      <Section id="services-overview" className="scroll-mt-24" data-testid="services-overview">
        <Container>
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-accent">
              Services Overview
            </p>
            <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
              Comprehensive Clinical Research Site Support
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              {SERVICES_INTRO.map((paragraph) => (
                <p key={paragraph.slice(0, 32)}>{paragraph}</p>
              ))}
            </div>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {SERVICE_DETAILS.map((service, index) => {
              const Icon = getIcon(service.icon);
              return (
                <Reveal key={service.id} delay={(index % 3) * 0.05}>
                  <Link
                    to={`#${service.id}`}
                    className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-primary/25 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Icon size={21} aria-hidden="true" />
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {service.group}
                      </span>
                    </div>
                    <h3 className="mt-5 text-xl font-semibold tracking-tight">
                      {service.title}
                    </h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {service.summary}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                      View service
                      <ChevronRight
                        size={16}
                        className="transition-transform group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </Section>

      {SERVICE_GROUPS.map((group, groupIndex) => (
        <div key={group.label} className={groupIndex % 2 === 0 ? "bg-muted" : "bg-background"}>
          <Container className="py-16 sm:py-20 lg:py-24">
            <div className="mb-12 flex items-center gap-4">
              <span
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${groupTone[group.label]}`}
              >
                {group.label}
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>
            <div className="space-y-12 lg:space-y-16">
              {group.items.map((service) => {
                const Icon = getIcon(service.icon);
                return (
                  <article
                    key={service.id}
                    id={service.id}
                    className="scroll-mt-28 rounded-3xl border border-border bg-background p-6 shadow-sm sm:p-8 lg:p-10"
                  >
                    <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
                      <div>
                        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                          <Icon size={23} aria-hidden="true" />
                        </span>
                        <h2 className="mt-5 text-balance text-3xl font-semibold tracking-tight">
                          {service.title}
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                          {service.intro}
                        </p>
                        <p className="mt-5 border-l-2 border-primary pl-4 text-sm leading-relaxed text-foreground/80">
                          {service.closing}
                        </p>
                      </div>
                      <ul className="grid content-start gap-3 sm:grid-cols-2">
                        {service.bullets.map((bullet) => (
                          <li
                            key={bullet}
                            className="flex items-start gap-3 rounded-xl border border-border bg-card px-4 py-3"
                          >
                            <Check
                              size={17}
                              className="mt-0.5 shrink-0 text-primary"
                              aria-hidden="true"
                            />
                            <span className="text-sm leading-relaxed text-foreground/85">
                              {bullet}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                );
              })}
            </div>
          </Container>
        </div>
      ))}

      <Section data-testid="services-partners">
        <Container>
          <SectionHeading
            eyebrow="Partner With Us"
            title="Support designed for both sides of the study partnership"
            description="MedCore coordinates the operational details that help community practices and study partners work together effectively."
          />
          <div className="mt-12 grid gap-7 lg:grid-cols-2">
            {PARTNER_SECTIONS.map((partner, index) => (
              <article
                key={partner.id}
                id={partner.id}
                className="scroll-mt-28 rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8"
              >
                <p className="text-sm font-semibold uppercase tracking-wider text-accent">
                  {partner.eyebrow}
                </p>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
                  {partner.title}
                </h2>
                <div className="mt-5 space-y-3 text-sm leading-relaxed text-muted-foreground">
                  {partner.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
                <ul className="mt-6 grid gap-2 sm:grid-cols-2">
                  {partner.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-2 text-sm">
                      <Check
                        size={16}
                        className="mt-0.5 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                      {bullet}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
                  {partner.closing}
                </p>
                <Button
                  as="link"
                  to={`/contact?inquiry=${partner.inquiry}#contact-form`}
                  className="mt-7"
                  variant={index === 0 ? "primary" : "secondary"}
                >
                  {partner.cta}
                  <ArrowRight size={17} aria-hidden="true" />
                </Button>
              </article>
            ))}
          </div>
        </Container>
      </Section>

      <Section id="why-medcore" muted className="scroll-mt-24" data-testid="why-medcore">
        <Container>
          <SectionHeading eyebrow="Why MedCore" title="Why Partner With MedCore?" />
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {WHY_MEDCORE.map((item) => {
              const Icon = getIcon(item.icon);
              return (
                <article key={item.title} className="rounded-2xl border border-border bg-background p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
                    <Icon size={21} aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 text-xl font-semibold">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </Container>
      </Section>

      <Section id="how-we-work" className="scroll-mt-24" data-testid="how-we-work">
        <Container>
          <SectionHeading eyebrow="Our Process" title="How We Work" />
          <ul className="relative mt-12 grid gap-6 lg:grid-cols-5">
            {HOW_WE_WORK.map((step) => (
              <li key={step.title} className="relative rounded-2xl border border-border bg-card p-6">
                <h3 className="text-xl font-semibold">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {step.description}
                </p>
              </li>
            ))}
          </ul>
        </Container>
      </Section>

      <section
        id="contact"
        className="scroll-mt-24 bg-brand-emerald py-16 text-white sm:py-20"
        data-testid="services-final-cta"
      >
        <Container className="text-center">
          <h2 className="text-balance text-3xl font-semibold sm:text-4xl">
            Bring Clinical Research Closer to the Communities You Serve
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-white/85">
            Whether you are a healthcare practice exploring clinical research or a
            pharmaceutical, biotechnology, or CRO partner looking for capable
            community-based sites, MedCore Research Inc. offers a structured and
            collaborative path forward.
          </p>
          <p className="mx-auto mt-3 max-w-3xl text-base leading-relaxed text-white/85">
            Let us build the operational foundation needed to transform promising
            research opportunities into well-managed clinical studies.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button as="link" to="/contact?inquiry=practice#contact-form" size="lg">
              Become a MedCore Partner Site
            </Button>
            <Button
              as="link"
              to="/contact?inquiry=study#contact-form"
              size="lg"
              variant="secondary"
            >
              Submit a Study Opportunity
            </Button>
            <Button
              as="link"
              to="/contact#contact-form"
              size="lg"
              variant="outline"
              className="border-white/40 bg-white/10 text-white hover:bg-white/20"
            >
              Contact Our Team
            </Button>
          </div>
        </Container>
      </section>
    </>
  );
}
